import {
  emptyRemoteLink,
  eventBodyRev,
  mapGoogleEvents,
  parseEventRev,
  systemTimeZone,
  type Calendar,
  type Task,
} from "@app/core";
import { GoogleSyncError, type GoogleEvent, type ListEventsOptions } from "@app/storage";
import type { CalendarApi } from "../provider.js";
import { applyUpdate, looseToOverride, upsertOverride } from "./apply-update.js";
import { reconcileSubtaskEdits } from "./subtask-edits.js";
import {
  buildEventBody,
  effectiveGoogleDirty,
  iso,
  loadTaskGraph,
  type TaskGraph,
} from "./task-graph.js";
import type { PullOptions, PullOutcome, ReconcileDeps } from "./types.js";

/** How far back a full (post-410) resync reaches. Client-driven sync isn't a
 *  full archive mirror; recent + future events are what matter on a phone. */
const FULL_SYNC_LOOKBACK_DAYS = 90;

/** INBOUND: pull a bound calendar's remote changes into local storage. */
export async function pullCalendar(
  api: CalendarApi,
  cal: Calendar,
  deps: ReconcileDeps,
  now: Date,
  options: PullOptions = {},
): Promise<PullOutcome> {
  const gcal = cal.external_calendar_id!;
  const floatingTimeZone = options.floatingTimeZone ?? systemTimeZone();
  /**
   * LOCAL-WINS guard. A task whose event graph carries local writes Google has
   * not acknowledged (`effectiveGoogleDirty`) is never overwritten or tombstoned
   * from Google — this pass's push sends the local content instead. The fact is
   * per row, so a calendar that has never pushed simply has every unsent row
   * win locally (and then push), rather than the old cursor's all-or-nothing
   * choice. A PULL-ONLY calendar disables the guard entirely: it never pushes,
   * so "the push will send it" is false and the remote is authoritative.
   */
  const localWins = cal.sync_direction !== "pull";
  let events: GoogleEvent[];
  let nextSyncToken: string | undefined;
  try {
    ({ events, nextSyncToken } = await listAllPages(api, gcal, { syncToken: cal.sync_token }));
  } catch (e) {
    if (e instanceof GoogleSyncError && e.status === 410) {
      // Expired syncToken → full resync from the lookback window.
      const timeMin = new Date(now.getTime() - FULL_SYNC_LOOKBACK_DAYS * 86_400_000).toISOString();
      ({ events, nextSyncToken } = await listAllPages(api, gcal, { timeMin }));
    } else {
      throw e;
    }
  }

  const result = mapGoogleEvents(events, {
    now: () => now,
    calendarId: cal.id,
    externalCalendarId: gcal,
  });

  /** External ids whose local task ended this pull IN SYNC with the remote —
   *  eligible for the canonical write-back and for subtask bracket edits. This
   *  set is PASS-INTERNAL: the push no longer needs a skip list, because a row
   *  the pull applied (or matched) is self-acknowledged and simply is not
   *  dirty, while a row with pending local writes is dirty and goes out. The
   *  old exported `touched` set doubled as the push's skip set, and one branch
   *  admitting a locally-dirty row silently suppressed that edit forever. */
  const inSync = new Set<string>();
  let changed = false;
  let created = 0;
  let updated = 0;
  let tombstoned = 0;
  let relinked = 0;
  let echoes = 0;
  let keptLocal = 0;

  let graph = await loadTaskGraph(deps);
  const index = buildRemoteIndex(graph, gcal);
  // Master events by id, for the echo check + the canonical write-back.
  const masterById = new Map<string, GoogleEvent>();
  for (const ev of events) if (!ev.recurringEventId) masterById.set(ev.id, ev);

  // External id → the LOCAL task, kept current as we create/update, so the
  // subtask reconcile below can resolve each event's parent.
  const localByExternal = new Map(index.byExternal);
  // Existing overrides for applyUpdate, loaded at most ONCE per pull instead of
  // per updated task. Safe to share: each task owns its rule, so one series'
  // inserts never invalidate another series' slice of this pool.
  let overridePool = graph.all.length > 0 ? [...graph.overridesByRule.values()].flat() : [];

  for (const task of result.assembly.tasks) {
    if (!task.external_id) continue;
    const extId = task.external_id;

    // RE-LINK BEFORE INSERT. A task whose `external_id` was lost (an old bug, a
    // restore, a peer that never learned it) must be reconnected to its event —
    // inserting instead is what turned one task into two, which then pushed two
    // events. `ical_uid` is Google's stable per-series UID and survives the id.
    let existing = index.byExternal.get(extId);
    if (!existing && task.ical_uid) {
      const candidate = index.byIcalUid.get(task.ical_uid);
      if (candidate && !candidate.external_id) {
        existing = candidate;
        await deps.tasks.setRemoteLink(candidate.id, {
          external_id: extId,
          ical_uid: task.ical_uid,
          etag: candidate.etag,
          source: "google",
          external_calendar_id: gcal,
        });
        const healed: Task = {
          ...candidate,
          external_id: extId,
          ical_uid: task.ical_uid,
          source: "google",
          external_calendar_id: gcal,
        };
        index.byExternal.set(extId, healed);
        localByExternal.set(extId, healed);
        existing = healed;
        changed = true;
        relinked++;
      }
    }

    // Tombstone-aware: a locally-deleted task is never resurrected from Google
    // (this also closes the 410 full-resync resurrection hole). Local delete
    // wins; the outbound push removes the remote event.
    if (existing?.deleted_at) continue;

    const rule = task.recurrence_id
      ? result.assembly.rules.find((r) => r.id === task.recurrence_id)
      : undefined;
    const ovs = result.assembly.overrides.filter((o) => o.recurrence_id === task.recurrence_id);

    if (!existing) {
      inSync.add(extId);
      // Inbound content IS Google's content — the inserts are born
      // google-acknowledged (and server-dirty, so peers learn the import).
      if (rule) await deps.recurrence.insert(rule, "google");
      await deps.tasks.insert(task, "google");
      for (const o of ovs) await deps.recurrenceOverrides.insert(o, "google");
      localByExternal.set(extId, task);
      changed = true;
      created++;
      continue;
    }

    if ((existing.etag ?? null) === (task.etag ?? null)) {
      // Unchanged REMOTELY — which is NOT the same as "in sync". This branch is
      // routinely reached for our OWN last write's delta echo (a pass's push
      // lands after its pull took the sync token, so the next pull returns it,
      // etag already adopted). A task that ALSO carries pending local writes is
      // still dirty and this same pass's push ships it; only a clean task is
      // eligible for write-back/extras.
      if (!effectiveGoogleDirty(graph, existing)) inSync.add(extId);
      continue; // unchanged remotely
    }

    // ECHO SUPPRESSION. The etag moved, but does the event actually SAY anything
    // different? Etags move for reasons that are not user edits — Google-side
    // normalisation, another client re-saving, our own write racing something —
    // and treating every one as "edited in Google" meant a full overwrite of the
    // local row (and a fresh `updated_at`, which then propagated the overwrite to
    // every other device). Compare the mapped remote against the local row on the
    // fields inbound would actually write; if nothing differs, adopt the new etag
    // and move on. NOTE this must be a comparison of CONTENT: the `tcRev` nonce
    // says what we last WROTE, and a human editing in Google leaves it stale, so
    // trusting it here would read a real edit as an echo.
    const localRule = existing.recurrence_id ? graph.ruleById.get(existing.recurrence_id) : undefined;
    if (inboundIsNoOp(existing, task, localRule, rule)) {
      // Same rule as the etag-match branch above: `inboundIsNoOp` compares the
      // fields INBOUND would write, but outbound dirtiness spans the whole event
      // graph — a pending EXDATE or subtask edit must still push, so a dirty
      // task is not "in sync" even when its own row matches.
      if (!effectiveGoogleDirty(graph, existing)) inSync.add(extId);
      await deps.tasks.setRemoteLink(existing.id, {
        external_id: existing.external_id ?? extId,
        ical_uid: existing.ical_uid ?? task.ical_uid,
        etag: task.etag ?? null,
        source: "google",
        external_calendar_id: gcal,
      });
      echoes++;
      continue;
    }

    // LOCAL EDIT WINS. The remote changed AND this task's event graph carries
    // local writes Google has not acknowledged. Inbound used to overwrite
    // unconditionally, which is how a just-dragged task snapped back to its old
    // time — and because the overwrite stamps a fresh `updated_at`, the reverted
    // state then propagated to every other device. Keep local (it is dirty, so
    // this pass's push sends it) and count a conflict. Google keeps its own
    // version history, so the shadowed remote edit is recoverable there; the
    // local edit would not be recoverable anywhere.
    if (localWins && effectiveGoogleDirty(graph, existing)) {
      keptLocal++;
      continue;
    }

    inSync.add(extId);
    await applyUpdate(deps, existing, task, rule, ovs, now, overridePool);
    changed = true;
    updated++;
  }

  // Apply the user's subtask BRACKET edits from each task event. The task list is
  // read ONCE and kept current across entries (an entry's create must stay visible
  // to a later entry's dup guard — parents nest, so subtrees overlap).
  if (result.extras.length > 0) {
    const allTasks = await deps.tasks.listAll();
    for (const entry of result.extras) {
      if (!inSync.has(entry.externalId)) continue; // local-wins / unresolved — don't touch
      const parent = localByExternal.get(entry.externalId);
      if (!parent || parent.is_event || parent.deleted_at) continue;
      if (await reconcileSubtaskEdits(deps, parent, entry, now, allTasks)) changed = true;
    }
  }

  // CANONICAL WRITE-BACK: re-emit our canonical shape for any event still in a
  // user-typed/raw form (a `[]`/`Task:` title, bare `[]` checkboxes) or stamped
  // with an older format version. The decision is the write nonce over the whole
  // body — never a text diff — so a Google whitespace reflow can't trigger a
  // write and the fixed point is reached in one cycle. Pull-only calendars are
  // read-only, so skip.
  if (cal.sync_direction !== "pull" && inSync.size > 0) {
    graph = await loadTaskGraph(deps);
    const freshByExternal = new Map<string, Task>();
    for (const x of graph.all) {
      // TASK events only. A plain event is the user's own calendar entry — we do
      // not own its shape, and rewriting it would stamp our machine properties
      // onto every event in every calendar we sync (and bump every etag for
      // every other client of that calendar).
      if (x.external_id && !x.deleted_at && !x.is_event) freshByExternal.set(x.external_id, x);
    }
    for (const extId of inSync) {
      const ev = masterById.get(extId);
      const t = freshByExternal.get(extId);
      if (!ev || !t) continue;
      const body = buildEventBody(graph, t, floatingTimeZone);
      const desiredRev = eventBodyRev(body);
      if (parseEventRev(ev.extendedProperties?.private) === desiredRev) continue;
      try {
        const patched = await api.patchEvent(gcal, extId, body, t.etag);
        // Adopt the new etag WITHOUT touching content or `updated_at` — this is
        // not a user edit and must not mark the task dirty for the next pass.
        await deps.tasks.setRemoteLink(t.id, {
          external_id: t.external_id ?? extId,
          ical_uid: t.ical_uid,
          etag: patched.etag ?? t.etag,
          source: "google",
          external_calendar_id: gcal,
        });
      } catch {
        /* best-effort normalization; a genuine local edit retries it next push */
      }
    }
    overridePool = [...graph.overridesByRule.values()].flat();
  }

  for (const extId of result.tombstones) {
    const existing = index.byExternal.get(extId);
    if (existing && !existing.deleted_at) {
      // LOCAL EDIT WINS over a remote delete too, matching the content guard
      // above and the 412 policy: a task whose event graph carries unacked local
      // writes is not tombstoned — it stays, the push's 412/`isGone` path
      // re-creates the event from the local copy, and a conflict is counted.
      // The shadowed remote delete is recoverable (Google keeps event history);
      // the local edit would not be recoverable anywhere. A clean task
      // tombstones as before — that delete is user intent and must propagate.
      if (localWins && effectiveGoogleDirty(graph, existing)) {
        keptLocal++;
        continue;
      }
      // The delete came FROM Google, so the write is google-acknowledged by
      // construction (and server-dirty, so the tombstone propagates to peers).
      await deps.tasks.update({ ...existing, deleted_at: iso(now), updated_at: iso(now) }, "google");
      // Drop the link: the remote event is gone, so a later RESTORE from Trash
      // must re-INSERT a fresh event rather than PATCH a cancelled one (whose
      // stale etag 412s and whose re-fetch maps back as a tombstone — nothing
      // would ever sync again). The unlink's own mut bump is acked by the
      // push's tombstone branch (its "nothing to send" exit).
      await deps.tasks.setRemoteLink(existing.id, emptyRemoteLink());
      changed = true;
      tombstoned++;
    }
  }

  for (const li of result.looseInstances) {
    const master = index.byExternal.get(li.masterExternalId);
    if (!master?.recurrence_id) continue; // master gone or not recurring locally — skip
    await upsertOverride(deps, master.recurrence_id, looseToOverride(master.recurrence_id, li, now));
    changed = true;
  }

  return {
    changed,
    syncToken: nextSyncToken ?? cal.sync_token ?? null,
    lastSyncedAt: iso(now),
    conflicts: keptLocal,
    stats: {
      fetched: events.length,
      created,
      updated,
      tombstoned,
      loose: result.looseInstances.length,
      relinked,
      echoes,
      keptLocal,
    },
  };
}

/** Fields inbound copies from the mapped remote onto the local row. If every one
 *  already matches, applying the update would be a no-op that only churns
 *  `updated_at` — see the call site. */
const INBOUND_FIELDS = [
  "title",
  "notes",
  "due_at",
  "deadline_at",
  "all_day",
  "duration_minutes",
  "priority",
  "custom_color",
  "is_event",
  "location",
  "tz",
  "status",
] as const;

function inboundIsNoOp(
  local: Task,
  mapped: Task,
  localRule: { rrule: string; dtstart: string; until: string | null } | undefined,
  mappedRule: { rrule: string; dtstart: string; until: string | null } | undefined,
): boolean {
  for (const f of INBOUND_FIELDS) {
    if ((local[f] ?? null) !== (mapped[f] ?? null)) return false;
  }
  const a = local.reminder_offsets ?? [];
  const b = mapped.reminder_offsets ?? [];
  if (a.length !== b.length || a.some((v, i) => v !== b[i])) return false;
  // A recurrence the remote no longer carries is a real change; so is a reshaped
  // or re-anchored rule.
  if (!localRule !== !mappedRule) return false;
  if (localRule && mappedRule) {
    if (
      localRule.rrule !== mappedRule.rrule ||
      localRule.dtstart !== mappedRule.dtstart ||
      (localRule.until ?? null) !== (mappedRule.until ?? null)
    ) {
      return false;
    }
  }
  return true;
}

interface RemoteIndex {
  byExternal: Map<string, Task>;
  byIcalUid: Map<string, Task>;
}

/**
 * Index local rows for inbound matching. `byExternal` spans EVERY task carrying
 * an external id, not just rows tagged `source === "google"` on this calendar —
 * a row whose `source` got lost used to miss the lookup and be re-inserted as a
 * duplicate. `byIcalUid` covers only UNLINKED rows on this calendar: they are the
 * re-link candidates.
 */
function buildRemoteIndex(graph: TaskGraph, gcal: string): RemoteIndex {
  const byExternal = new Map<string, Task>();
  const byIcalUid = new Map<string, Task>();
  for (const t of graph.all) {
    if (t.external_id) byExternal.set(t.external_id, t);
    else if (t.ical_uid && !t.deleted_at && (!t.external_calendar_id || t.external_calendar_id === gcal)) {
      // First writer wins so re-linking is deterministic across devices.
      if (!byIcalUid.has(t.ical_uid)) byIcalUid.set(t.ical_uid, t);
    }
  }
  return { byExternal, byIcalUid };
}

async function listAllPages(
  api: CalendarApi,
  calendarId: string,
  base: { syncToken?: string | null; timeMin?: string },
): Promise<{ events: GoogleEvent[]; nextSyncToken: string | undefined }> {
  const events: GoogleEvent[] = [];
  let pageToken: string | undefined;
  let nextSyncToken: string | undefined;
  do {
    const opts: ListEventsOptions = { ...base };
    if (pageToken) opts.pageToken = pageToken;
    const page = await api.listEvents(calendarId, opts);
    events.push(...page.items);
    pageToken = page.nextPageToken;
    nextSyncToken = page.nextSyncToken ?? nextSyncToken;
  } while (pageToken);
  return { events, nextSyncToken };
}
